// What should printing do for this outlet — the single answer every POS print site
// asks for, so the sale receipt, the sale return and the direct return cannot drift
// apart the way the four inlined `receiptType === 2 || === 3` forks did.
//
// Shops that run a thermal roll and an A4 printer side by side decide at the counter
// which one a customer gets, so Outlet > Settings can turn the silent print into a
// prompt (AskReceiptFormatOnSale) and say which printers actually exist
// (OfferThermalReceipt / OfferA4Receipt / OfferA5Receipt).

// Same values as Outlet.ReceiptType — 1=Thermal, 2=A4, 3=A5.
export const RECEIPT_FORMAT = { thermal: 1, a4: 2, a5: 3 };

// Offer order is fixed (thermal, A4, A5) so the buttons never reshuffle under a
// cashier's finger between sales.
const OFFERS = [
  { format: RECEIPT_FORMAT.thermal, flag: "offerThermalReceipt" },
  { format: RECEIPT_FORMAT.a4, flag: "offerA4Receipt" },
  { format: RECEIPT_FORMAT.a5, flag: "offerA5Receipt" },
];

/**
 * @param {object} outlet the hydrated outlet off the sale / activeReg blob
 * @returns {{ask: boolean, formats: number[], preferred: number}}
 *   ask       – show the format prompt instead of printing silently
 *   formats   – the formats to offer, in display order
 *   preferred – the format to pre-select, and the one printed when ask is false
 */
export const resolveReceiptFormats = (outlet) => {
  // The outlet's configured format stays the default in both modes. Anything
  // unrecognised (or a tenant whose DB predates these columns) falls back to
  // thermal, which is the entity default.
  const configured = [RECEIPT_FORMAT.thermal, RECEIPT_FORMAT.a4, RECEIPT_FORMAT.a5]
    .includes(outlet?.receiptType)
    ? outlet.receiptType
    : RECEIPT_FORMAT.thermal;

  const formats = OFFERS.filter((o) => outlet?.[o.flag]).map((o) => o.format);

  // A prompt with one button is not a choice, and one with none is a dead end.
  // Both fall back to a silent print — as does an outlet on a tenant DB that has
  // not been synced yet, where the toggle reads undefined. That is the whole
  // backward-compatibility guarantee: absent columns == today's behaviour.
  const ask = outlet?.askReceiptFormatOnSale === true && formats.length > 1;

  return {
    ask,
    formats: ask ? formats : [configured],
    preferred: ask && !formats.includes(configured) ? formats[0] : configured,
  };
};

/** DOM id of the hidden mount holding one format's rendered receipt. */
export const receiptDivId = (baseId, format) => `${baseId}_${format}`;
